import type { DrilldownResult } from "./model.js";

export type FetchFunction = (url: string | URL, init?: RequestInit) => Promise<Response>;
export type EntityLabelKind = "member" | "channel";

type EntityLabelEntry = {
    id: string;
    name?: string;
    user?: {
        globalName?: string | null;
        username?: string | null;
    };
};

const DrilldownCache = new Map<string, DrilldownResult>();
const DrilldownInFlight = new Map<string, Promise<DrilldownResult>>();
const EntityLabelCaches = {
    member  : new Map<string, string>(),
    channel : new Map<string, string>(),
};
const EntityLabelInFlight = {
    member  : new Map<string, Promise<void>>(),
    channel : new Map<string, Promise<void>>(),
};

export async function getDrilldown(fetch: FetchFunction, key: string, url: string): Promise<DrilldownResult> {
    const cached = DrilldownCache.get(key);

    if (cached)
        return cached;

    const inFlight = DrilldownInFlight.get(key);

    if (inFlight)
        return inFlight;

    const promise = fetch(url)
        .then((res) => res.json())
        .then((drilldown: DrilldownResult) => {
            DrilldownCache.set(key, drilldown);
            return drilldown;
        })
        .finally(() => {
            DrilldownInFlight.delete(key);
        });

    DrilldownInFlight.set(key, promise);
    return promise;
}

export async function fetchMissingEntityLabels(fetch: FetchFunction, kind: EntityLabelKind, ids: string[]): Promise<void> {
    const cache = EntityLabelCaches[kind];
    const inFlight = EntityLabelInFlight[kind];
    const missingIds = [ ...new Set(ids) ].filter((id) => !cache.has(id));

    if (!missingIds.length)
        return;

    const pendingIds = missingIds.filter((id) => !inFlight.has(id));

    if (pendingIds.length) {
        const endpoint = kind === "channel" ? "/api/channels" : "/api/members";
        const promise = fetch(endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(pendingIds),
        })
            .then((res) => res.json())
            .then((entries: EntityLabelEntry[]) => {
                for (const entry of entries) {
                    if (kind === "channel")
                        cache.set(entry.id, entry.name || entry.id);
                    else
                        cache.set(entry.id, entry.user?.globalName || entry.user?.username || entry.id);
                }

                for (const id of pendingIds)
                    if (!cache.has(id))
                        cache.set(id, id);
            })
            .finally(() => {
                for (const id of pendingIds)
                    inFlight.delete(id);
            });

        for (const id of pendingIds)
            inFlight.set(id, promise);
    }

    await Promise.all(missingIds.map((id) => inFlight.get(id)).filter((promise): promise is Promise<void> => Boolean(promise)));
}

export function getCachedEntityLabel(kind: EntityLabelKind, id: string): string | undefined {
    return EntityLabelCaches[kind].get(id);
}
